import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { Box } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import { noop } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import Menu, { DropdownButton, MenuItem } from '@adsk/alloy-react-menu';
import Tooltip from '@adsk/alloy-react-tooltip';

import getPluginKey from './helpers/getPluginKey';
import { pluginShape } from './helpers/propTypes';
import TypeButtonLabel from './TypeButtonLabel';

const tooltipOffset = [-5, -5];

const CustomMenuItem = React.forwardRef(({ tooltipContent, ...props }, ref) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  return (
    <Box style={props.style}>
      <Tooltip
        content={
          props.disabled
            ? tooltipContent || T.translate('DISABLED_NO_PERMISSIONS')
            : null
        }
        offset={tooltipOffset}
      >
        <MenuItem
          ref={ref}
          {...props}
          style={[
            {
              ':disabled': {
                pointerEvents: 'none',
              },
            },
          ]}
        />
      </Tooltip>
    </Box>
  );
});

CustomMenuItem.displayName = 'CustomMenuItem';

CustomMenuItem.propTypes = {
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  disabled: PropTypes.bool.isRequired,
  tooltipContent: PropTypes.node,
};

const CustomMenu = React.forwardRef((props, ref) => (
  <Menu
    ref={ref}
    {...props}
    renderItem={(props) => <CustomMenuItem {...props} />}
  />
));

CustomMenu.displayName = 'CustomMenu';

const hasMoreThanOnePluginInGroup = (plugin, plugins) =>
  plugins.filter(
    ({ nestedOptions }) =>
      nestedOptions && nestedOptions.key === plugin.nestedOptions.key,
  ).length > 1;

const createOrUpdateNestedOption = (options, plugin, option) => {
  const parentOption = options.find(
    (option) => plugin.nestedOptions.key === option.key,
  );

  const nestedCategory = {
    key: plugin.nestedOptions.nestedCategoryKey,
    itemOptions: [option],
    multiSelection: false,
    showSelection: false,
  };

  if (!parentOption) {
    options.push({
      key: plugin.nestedOptions.key,
      label: plugin.nestedOptions.label,
      renderIcon: plugin.nestedOptions.renderIcon,
      disabled: false,
      nested: [nestedCategory],
    });
  } else {
    const currentCategoryOption = parentOption.nested.find(
      (item) => item && item.key === plugin.nestedOptions.nestedCategoryKey,
    );
    currentCategoryOption
      ? currentCategoryOption.itemOptions.push(option)
      : parentOption.nested.push(nestedCategory);
  }
};

const ReferencePickerTypeButton = ({
  plugins,
  openPickerModal,
  children,
  loadingPlugins,
}) => {
  const options = useMemo(() => {
    const options = [];
    plugins.forEach((plugin) => {
      const disabled = !!plugin.disablePickerWithMessage;
      const tooltipContent = plugin.disablePickerWithMessage;

      const option = {
        key: getPluginKey(plugin),
        label: (
          <TypeButtonLabel
            plugin={plugin}
            disabled={disabled}
            showIcon={
              !(
                plugin.nestedOptions &&
                hasMoreThanOnePluginInGroup(plugin, plugins)
              )
            }
          />
        ),
        disabled,
        tooltipContent,
      };

      const hasCustomOptionStyle = !!plugin.CustomOptionStyle;
      if (hasCustomOptionStyle) {
        option.style = plugin.CustomOptionStyle;
      }

      if (
        plugin.nestedOptions &&
        hasMoreThanOnePluginInGroup(plugin, plugins)
      ) {
        createOrUpdateNestedOption(options, plugin, option);
      } else {
        options.push(option);
      }
    });
    return options;
  }, [plugins]);

  if (loadingPlugins) {
    return (
      <Button
        onClick={noop}
        disabled
        loading
        data-testid="ReferencePickerView__add-reference-button"
      >
        {children}
      </Button>
    );
  }
  if (!plugins.length) {
    return null;
  }
  if (plugins.length === 1) {
    const disabled = !!plugins[0].disablePickerWithMessage;
    const tooltipContent = plugins[0].disablePickerWithMessage;
    return (
      <Tooltip content={tooltipContent}>
        <Button
          onClick={() => openPickerModal(plugins[0])}
          data-testid="ReferencePickerView__add-reference-button"
          disabled={disabled}
        >
          {children}
        </Button>
      </Tooltip>
    );
  }

  const keyToPlugin = plugins.reduce((acc, plugin) => {
    acc[getPluginKey(plugin)] = plugin;
    return acc;
  }, {});

  return (
    <DropdownButton
      options={options}
      onChange={([value]) => openPickerModal(keyToPlugin[value])}
      renderContent={(props) => <CustomMenu {...props} />}
      data-testid="ReferencePickerTypeButton"
      value={[]}
    >
      {children}
    </DropdownButton>
  );
};

ReferencePickerTypeButton.propTypes = {
  /**
   * The plugins we want to allow linking to
   */
  plugins: PropTypes.arrayOf(pluginShape).isRequired,
  /**
   * Handler to open reference picker modal
   */
  openPickerModal: PropTypes.func.isRequired,
  /**
   * The children to render inside the button
   */
  children: PropTypes.node.isRequired,
  /**
   * Whether to force loading state
   */
  loadingPlugins: PropTypes.bool,
};

export default ReferencePickerTypeButton;
